/**
 * Contexto para compartir el estado de animación del avatar
 * entre todos los componentes
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { AnimationState } from '../types/animation';

interface AvatarAnimationContextType {
  // Estados legacy (mantener compatibilidad)
  isHablando: boolean;
  setHablando: (estado: boolean) => void;
  saludar: () => void;
  activateAnimation: (numero: number) => void;
  
  // Nuevos estados
  currentState: AnimationState;
  setState: (state: AnimationState) => void;
  isListening: boolean;
  setListening: (estado: boolean) => void;
  isThinking: boolean;
  setThinking: (estado: boolean) => void;
  isSaludando: boolean;
  setSaludando: (estado: boolean) => void;
  resetFirstInteraction: () => void;
}

const AvatarAnimationContext = createContext<AvatarAnimationContextType | undefined>(undefined);

export function AvatarAnimationProvider({ children }: { children: ReactNode }) {
  const [isHablando, setIsHablando] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false); // Nuevo: indica si hay audio reproduciéndose
  const [currentState, setCurrentState] = useState<AnimationState>('idle');
  const [isListening, setIsListening] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isSaludando, setIsSaludando] = useState(false);
  const hasSpokenBeforeRef = useRef(false); // Ref para acceder al valor actual sin depender del closure
  const greetingTimerRef = useRef<NodeJS.Timeout | null>(null); // Timer para greeting

  // Sincronizar currentState con los estados booleanos
  // Prioridad: greeting > talking (si isSpeaking) > listening > thinking > idle
  useEffect(() => {
    if (isSaludando) {
      setCurrentState('greeting');
    } else if (isSpeaking) {
      // Si está reproduciendo audio y greeting terminó, usar talking
      setCurrentState('talking');
    } else if (isListening) {
      setCurrentState('listening');
    } else if (isThinking) {
      setCurrentState('thinking');
    } else {
      setCurrentState('idle');
    }
  }, [isSaludando, isSpeaking, isListening, isThinking]);

  // Cleanup: limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (greetingTimerRef.current) {
        clearTimeout(greetingTimerRef.current);
      }
    };
  }, []);

  const setSaludando = useCallback((estado: boolean) => {
    setIsSaludando(estado);
  }, []);

  const setHablando = useCallback((estado: boolean) => {
    // Controlar el estado de reproducción de audio
    setIsSpeaking(estado);
    
    // Limpiar timer anterior si existe
    if (greetingTimerRef.current) {
      clearTimeout(greetingTimerRef.current);
      greetingTimerRef.current = null;
    }
    
    if (estado) {
      // Si es la primera vez que habla, activar greeting
      if (!hasSpokenBeforeRef.current) {
        setIsSaludando(true);
        setIsHablando(false);
        hasSpokenBeforeRef.current = true;
        
        // Duración de greeting: 16 frames a 10fps = 1.6s, usar 1.8s
        const greetingDuration = 1800; // ms
        
        greetingTimerRef.current = setTimeout(() => {
          setIsSaludando(false);
          // Si isSpeaking sigue true, el useEffect cambiará a talking automáticamente
          greetingTimerRef.current = null;
        }, greetingDuration);
      } else {
        // Si ya habló antes, ir directo a talking
        setIsSaludando(false);
        setIsHablando(true);
      }
    } else {
      // Al desactivar, limpiar todos los estados
      setIsSaludando(false);
      setIsHablando(false);
    }
  }, []);

  const setListening = useCallback((estado: boolean) => {
    setIsListening(estado);
  }, []);

  const setThinking = useCallback((estado: boolean) => {
    setIsThinking(estado);
  }, []);

  const setState = useCallback((state: AnimationState) => {
    setCurrentState(state);
    // Actualizar estados booleanos para compatibilidad
    setIsHablando(state === 'talking');
    setIsListening(state === 'listening');
    setIsThinking(state === 'thinking');
    setIsSaludando(state === 'greeting');
    // Si se activa talking, marcar que ya habló antes
    if (state === 'talking' && !hasSpokenBeforeRef.current) {
      hasSpokenBeforeRef.current = true;
    }
  }, []);

  // Resetear el estado de "primera vez" cuando sea necesario (ej: volver al menú)
  const resetFirstInteraction = useCallback(() => {
    hasSpokenBeforeRef.current = false;
    // Limpiar timer si existe
    if (greetingTimerRef.current) {
      clearTimeout(greetingTimerRef.current);
      greetingTimerRef.current = null;
    }
  }, []);

  const saludar = useCallback(() => {
    setSaludando(true);
  }, [setSaludando]);

  const activateAnimation = useCallback((numero: number) => {
    // Mapear números a estados (para compatibilidad)
    const stateMap: Record<number, AnimationState> = {
      0: 'idle',
      1: 'talking',
      2: 'listening',
      3: 'thinking',
      4: 'greeting',
      5: 'reaction',
    };
    
    const state = stateMap[numero] || 'idle';
    setState(state);
  }, [setState]);

  const value: AvatarAnimationContextType = {
    isHablando,
    setHablando,
    saludar,
    activateAnimation,
    currentState,
    setState,
    isListening,
    setListening,
    isThinking,
    setThinking,
    isSaludando,
    setSaludando,
    resetFirstInteraction,
  };

  return (
    <AvatarAnimationContext.Provider value={value}>
      {children}
    </AvatarAnimationContext.Provider>
  );
}

export function useAvatarAnimation(): AvatarAnimationContextType {
  const context = useContext(AvatarAnimationContext);
  if (context === undefined) {
    throw new Error('useAvatarAnimation debe usarse dentro de AvatarAnimationProvider');
  }
  return context;
}
